import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

/**
 * SolarHeatmap — renders a Leaflet.heat layer weighted by each site's solar score.
 *
 * leaflet.heat has no official React wrapper, so we load it dynamically via
 * a <script> tag the first time, then construct L.heatLayer imperatively.
 *
 * Props:
 *   solarData  – Map<"lat,lng", { irradiance, temperature, score, ... }>
 *   sites      – array of raw site objects (for lat/lng extraction)
 */
const LEAFLET_HEAT_CDN =
    'https://leaflet.github.io/Leaflet.heat/dist/leaflet-heat.js';

function loadLeafletHeat() {
    return new Promise((resolve, reject) => {
        if (window.L && window.L.heatLayer) return resolve();
        if (document.getElementById('leaflet-heat-script')) {
            // Script already injected — wait for it
            const check = setInterval(() => {
                if (window.L && window.L.heatLayer) {
                    clearInterval(check);
                    resolve();
                }
            }, 100);
            return;
        }
        const script = document.createElement('script');
        script.id = 'leaflet-heat-script';
        script.src = LEAFLET_HEAT_CDN;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

const SolarHeatmap = ({ sites, solarData }) => {
    const map = useMap();
    const heatLayerRef = useRef(null);

    useEffect(() => {
        if (!map || !sites || sites.length === 0 || !solarData || solarData.size === 0) return;

        let cancelled = false;

        loadLeafletHeat().then(() => {
            if (cancelled) return;

            // Build [lat, lng, intensity] tuples – intensity normalised 0-1
            const MAX_SCORE = 7; // ~max realistic kWh/m²/day after derating
            const points = [];

            for (const site of sites) {
                const lat = parseFloat(site.Latitude || site.latitude);
                const lng = parseFloat(site.Longitude || site.longitude);
                if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) continue;

                const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
                const solar = solarData.get(key);
                const score = solar?.score ?? 0;
                points.push([lat, lng, Math.min(score / MAX_SCORE, 1)]);
            }

            if (heatLayerRef.current) {
                map.removeLayer(heatLayerRef.current);
            }

            heatLayerRef.current = window.L.heatLayer(points, {
                radius: 35,
                blur: 25,
                maxZoom: 10,
                gradient: {
                    0.0: '#1e3a5f',   // Poor — deep blue
                    0.3: '#EF4444',   // Poor/Moderate — red
                    0.55: '#FB923C',  // Moderate — orange
                    0.75: '#60A5FA',  // Silver — light blue
                    1.0: '#F59E0B',   // Gold — amber
                },
            }).addTo(map);

            // leaflet.heat doesn't support pane option — directly fix its canvas
            // so it doesn't block pointer events on site markers
            const fixCanvas = () => {
                const canvas = heatLayerRef.current?._canvas;
                if (canvas) {
                    canvas.style.pointerEvents = 'none';
                    canvas.style.zIndex = '-1';
                }
            };
            fixCanvas();
            // Also fix after next redraw in case canvas is recreated
            map.on('moveend zoomend', fixCanvas);
        });

        return () => {
            cancelled = true;
            if (heatLayerRef.current && map) {
                try { map.removeLayer(heatLayerRef.current); } catch (_) {}
                heatLayerRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, sites, solarData]);

    return null;
};

export default SolarHeatmap;
